// src/components/Contact/ContactForm.tsx
//
// The connect form and everything that makes it work: validation, the email
// call, the loading state, the error state and the success message.
//
// This is the single implementation of that logic. It is rendered in two places
// — inside ContactModal, and on the /contacts route that is kept for bookmarks —
// and both get this component, not a copy. Nothing here knows whether it is being
// displayed as a window or as a page.
//
// The one thing that used to live in the page wrapper was the post-success
// redirect to "/". That has been replaced by the optional onSuccess callback, so
// the caller decides what happens after a send: a modal closes itself, a page
// stays where it is. Neither is forced back to the home page.
import { useId } from 'react';
import { FaPaperPlane, FaTimesCircle } from 'react-icons/fa';

import ContactActions from '../ContactActions';
import Loader from '../Loader';
import SuccessModal from '../SuccessModal';
import { sendConnectEmail } from '../../services/emailService';
import { useEmailForm, validateConnectForm } from '../../hooks/useEmailForm';
import { RequiredMark } from '../../styles/formStyles';
import {
  Actions,
  ErrorMessage,
  Field,
  Form,
  Input,
  Label,
  Lead,
  Row,
  SubmitButton,
  TextArea,
} from './ContactForm.styles';

export interface ContactFormProps {
  /**
   * Fired once the success message has been shown and has run its course, i.e.
   * after SuccessModal auto-closes. ContactModal passes the modal's onClose here
   * so the window shuts only after the visitor has seen the confirmation. When
   * omitted the success message simply dismisses itself and the form stays.
   */
  onSuccess?: () => void;
  /** How long the success message stays up, in milliseconds. */
  successDuration?: number;
}

const ContactForm = ({ onSuccess, successDuration = 4000 }: ContactFormProps) => {
  /**
   * Field ids are generated rather than hardcoded. The form can legitimately be
   * mounted more than once on a page — the /contacts route renders it, and a
   * modal opened from that page would render it again — and duplicated DOM ids
   * break the label/control association and any document.getElementById lookup.
   *
   * The name attributes are a different matter and are load-bearing. This form
   * is older than the query form and posts short names, so validateConnectForm
   * and sendConnectEmail are fed the mapped values in onSubmit below rather than
   * renaming the fields. Changing the names would change the email subject and
   * body that validateConnectForm reads.
   */
  const fieldId = useId();
  const id = (field: string) => `${fieldId}-${field}`;

  const { isLoading, errorMessage, handleSubmit, showSuccess, closeSuccessModal } = useEmailForm({
    onSubmit: async (payload: any) => {
      // Validate form data
      const errors = validateConnectForm({
        senderName: payload.name,
        senderEmail: payload.email,
        message: payload.message,
      });
      if (Object.keys(errors).length > 0) {
        throw new Error(Object.values(errors).join(', '));
      }

      // Send email with CONNECT type
      await sendConnectEmail(payload.name, payload.email, payload.message);
    },
  });

  return (
    <>
      {/* The shared Loader is a full-screen overlay at z-index 9999, so it sits
          above the window while a send is in flight. That is the intended
          layering: it also blocks a second submit from being started. */}
      <Loader isVisible={isLoading && !showSuccess} message="Sending your message..." />

      <SuccessModal
        isVisible={showSuccess}
        onClose={closeSuccessModal}
        onAutoClose={onSuccess}
        autoCloseDuration={successDuration}
      />

      <Lead>
        Whether you have a question, want to start a project, or just want to say
        hi — my inbox is open!
      </Lead>

      {/* Carried over from the old page so a visitor who wants to mail or call
          directly does not have to fill the form in first. */}
      <Actions>
        <ContactActions />
      </Actions>

      {errorMessage && (
        <ErrorMessage role="alert">
          <FaTimesCircle aria-hidden="true" />
          <span>{errorMessage}</span>
        </ErrorMessage>
      )}

      <Form onSubmit={handleSubmit} noValidate={false}>
        <Row>
          <Field>
            <Label htmlFor={id('name')}>
              Your Name
              <RequiredMark>*</RequiredMark>
            </Label>
            <Input
              id={id('name')}
              name="name"
              type="text"
              placeholder="Your Name"
              autoComplete="name"
              required
              disabled={isLoading}
              /* Focus lands here when the window opens, rather than on the
                 close control, because this is what the visitor came to do. */
              data-modal-autofocus=""
            />
          </Field>
          <Field>
            <Label htmlFor={id('email')}>
              Your Email
              <RequiredMark>*</RequiredMark>
            </Label>
            <Input
              id={id('email')}
              name="email"
              type="email"
              placeholder="you@example.com"
              autoComplete="email"
              required
              disabled={isLoading}
            />
          </Field>
        </Row>

        <Field>
          <Label htmlFor={id('message')}>
            Your Message
            <RequiredMark>*</RequiredMark>
          </Label>
          <TextArea
            id={id('message')}
            name="message"
            placeholder="What would you like to talk about?"
            required
            disabled={isLoading}
          />
        </Field>

        <SubmitButton type="submit" disabled={isLoading}>
          <FaPaperPlane aria-hidden="true" />
          {isLoading ? 'Sending...' : 'Send Message'}
        </SubmitButton>
      </Form>
    </>
  );
};

export default ContactForm;
